import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden/Hidden";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ELEMENTS, TYPOGRAPHY } from "@tSurya/theme/design";
import GVLeftArrow from "@tSurya/theme/styleguide/icons/GVLeftArrow";
import GVRightArrow from "@tSurya/theme/styleguide/icons/GVRightArrow";
import { isThemeActive, THEME_IDS } from "@tSurya/theme/themeHelper";
import * as cssLinter from "css";
import useEmblaCarousel from "embla-carousel-react";
import uniqueId from "lodash/uniqueId";
import React, { useCallback, useEffect, useState } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    backgroundColor = "backgroundColor",
    textColor = "textColor",
    images = "images",
    customCSS = "customCSS",
}

interface ImageInterface {
    image?: string;
    altText?: string;
    link?: LinkFieldValue;
}

interface ImagesModel {
    fields: ImageInterface;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.backgroundColor]: string;
        [fields.textColor]: string;
        [fields.images]: ImagesModel[];
        [fields.customCSS]: string;
    };
}

type Props = OwnProps;

export interface SurImageGalleryStyles {
    backgroundContainer?: InjectableCss;
    container?: InjectableCss;
    titleContainer?: InjectableCss;
    title?: TypographyProps;
    galleryContainer?: GridContainerProps;
    galleryItem?: GridItemProps;
    imageLink?: InjectableCss;
    image?: InjectableCss;
    // Carousel styles
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    arrowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    nextArrowWrapper?: InjectableCss;
    prevArrowButton?: InjectableCss;
    nextArrowButton?: InjectableCss;
}

export const imageGalleryStyles: SurImageGalleryStyles = {
    backgroundContainer: {
        css: css`
            width: 100%;
            padding: 80px 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 40px 0;
                    `,
                    css`
                        padding: 50px 16px;
                    `,
                    css`
                        padding: 60px 16px;
                    `,
                    css`
                        padding: 60px 16px;
                    `,
                    null,
                ])}
        `,
    },
    container: {
        css: css`
            width: 100%;
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0 20px;
                    `,
                    css`
                        padding: 0 30px;
                    `,
                    css`
                        padding: 0 40px;
                    `,
                    css`
                        padding: 0 20px;
                    `,
                    css`
                        padding: 0;
                    `,
                ])}
        `,
    },
    titleContainer: {
        css: css`
            margin-bottom: 50px;
            text-align: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 30px;
                    `,
                    css`
                        margin-bottom: 35px;
                    `,
                    css`
                        margin-bottom: 40px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    title: {
        variant: "h1",
        css: css`
            margin-bottom: 0;
        `,
    },
    galleryContainer: {
        css: css`
            width: 100%;
            display: grid;
            grid-template-columns: repeat(1, 1fr);
            gap: 16px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: repeat(1, 1fr);
                        gap: 16px;
                    `,
                    css`
                        grid-template-columns: repeat(2, 1fr);
                        gap: 20px;
                    `,
                    css`
                        grid-template-columns: repeat(2, 1fr);
                        gap: 20px;
                    `,
                    css`
                        grid-template-columns: repeat(3, 1fr);
                        gap: 30px;
                    `,
                    css`
                        grid-template-columns: repeat(3, 1fr);
                        gap: 30px;
                    `,
                ])}
        `,
    },
    galleryItem: {
        css: css`
            width: 100%;
        `,
    },
    imageLink: {
        css: css`
            display: block;
            width: 100%;
            text-decoration: none;
            cursor: pointer;
            transition: all 0.3s ease-in-out;
            
            &:hover {
                transform: scale(1.01);
                box-shadow: 0 20px 25px rgba(0, 0, 0, .5);
            }
        `,
    },
    image: {
        css: css`
            width: 100%;
            height: auto;
            object-fit: cover;
            aspect-ratio: 1/1;
            max-width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        aspect-ratio: 4/3;
                    `,
                    css`
                        aspect-ratio: 4/3;
                    `,
                    css`
                        aspect-ratio: 1/1;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            position: relative;
            overflow: hidden;
        `,
    },
    carouselContainer: {
        css: css`
            display: flex;
            padding: 0 20px;
        `,
    },
    carouselSlide: {
        css: css`
            position: relative;
            flex: 0 0 calc(100% - 40px);
            max-width: calc(100% - 40px);
            margin-right: 16px;
        `,
    },
    arrowWrapper: {
        css: css`
            position: relative;
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 20px;
            padding: 0 20px;
        `,
    },
    prevArrowWrapper: {
        css: css`
            position: absolute;
            left: 10px;
            top: 50%;
            transform: translateY(-50%);
            z-index: 10;
        `,
    },
    nextArrowWrapper: {
        css: css`
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            z-index: 10;
        `,
    },
    prevArrowButton: {
        css: css`
            background-color: #FFFFFF;
            border: ${ELEMENTS.carouselArrows.border};
            width: ${ELEMENTS.carouselArrows.size};
            height: ${ELEMENTS.carouselArrows.size};
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: ${ELEMENTS.carouselArrows.boxShadow};
            flex-shrink: 0;
            min-width: ${ELEMENTS.carouselArrows.size};
            min-height: ${ELEMENTS.carouselArrows.size};
            padding: ${ELEMENTS.carouselArrows.padding};
            > span {
                top: ${ELEMENTS.carouselArrows.iconTop} !important;
            }
            &:hover {
                background: #FFFFFF;
                border: ${ELEMENTS.carouselArrows.border};
            }
            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        `,
    },
    nextArrowButton: {
        css: css`
            background-color: #FFFFFF;
            border: ${ELEMENTS.carouselArrows.border};
            width: ${ELEMENTS.carouselArrows.size};
            height: ${ELEMENTS.carouselArrows.size};
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: ${ELEMENTS.carouselArrows.boxShadow};
            flex-shrink: 0;
            min-width: ${ELEMENTS.carouselArrows.size};
            min-height: ${ELEMENTS.carouselArrows.size};
            padding: ${ELEMENTS.carouselArrows.padding};
            > span {
                top: ${ELEMENTS.carouselArrows.iconTop} !important;
            }
            &:hover {
                background: #FFFFFF;
                border: ${ELEMENTS.carouselArrows.border};
            }
            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        `,
    },
};

const styles = imageGalleryStyles;

const defaultCustomCss = `
.gallery-container {
    
}

.gallery-item {
    
}

.gallery-image {
    
}
`;

const SurImageGalleryWidget: React.FC<Props> = ({ fields }) => {
    const imageLinks = useGetLinks(fields.images, o => o.fields.link || { value: "", type: "Page" });
    const isGlobalViewsTheme = isThemeActive(THEME_IDS.GLOBAL_VIEWS);

    // Carousel setup
    const [emblaRef, embla] = useEmblaCarousel();
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const setCanScroll = useCallback(() => {
        if (!embla) {
            return;
        }
        setCanScrollPrev(embla.canScrollPrev());
        setCanScrollNext(embla.canScrollNext());
    }, [embla]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.reInit({
            align: "start",
            containScroll: "trimSnaps",
            dragFree: false,
            loop: false,
        });

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);
        setCanScroll();

        return () => {
            embla.off("init", setCanScroll);
            embla.off("select", setCanScroll);
        };
    }, [embla, fields.images, setCanScroll]);

    const customWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const backgroundContainerStyles = {
        css: css`
            ${styles.backgroundContainer?.css || ""}
            background-color: ${fields.backgroundColor || "transparent"};
        `,
    };

    const titleStyles = {
        ...styles.title,
        color: fields.textColor || getColor("text.main")({}),
    };

    return (
        <StyledWrapper {...customWrapper}>
            <StyledWrapper {...backgroundContainerStyles} className="gallery-container">
                <StyledWrapper {...styles.container} className="max-width-container">
                    {fields.title && (
                        <StyledWrapper {...styles.titleContainer}>
                            <Typography {...titleStyles} as="h2" role="heading">{fields.title}</Typography>
                        </StyledWrapper>
                    )}

                    {fields.images && fields.images.length > 0 && (
                        <>
                            <Hidden below="sm">
                                <StyledWrapper {...styles.galleryContainer}>
                                    {fields.images.map((item: ImagesModel, index: number) => (
                                        <StyledWrapper
                                            key={uniqueId()}
                                            {...styles.galleryItem}
                                            className="gallery-item"
                                        >
                                            {item.fields.image && (
                                                (item.fields.link && item.fields.link.value && imageLinks.links[index]?.url) ? (
                                                    <Link
                                                        href={imageLinks.links[index].url}
                                                        {...styles.imageLink}
                                                    >
                                                        <LazyImage
                                                            src={item.fields.image}
                                                            altText={item.fields.altText || `Gallery image ${index + 1}`}
                                                            {...styles.image}
                                                            className="gallery-image"
                                                        />
                                                    </Link>
                                                ) : (
                                                    <LazyImage
                                                        src={item.fields.image}
                                                        altText={item.fields.altText || `Gallery image ${index + 1}`}
                                                        {...styles.image}
                                                        className="gallery-image"
                                                    />
                                                )
                                            )}
                                        </StyledWrapper>
                                    ))}
                                </StyledWrapper>
                            </Hidden>

                            <Hidden above="xs">
                                <StyledWrapper {...styles.carouselWrapper}>
                                    <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                                        <StyledWrapper
                                            css={css`
                                                display: flex;
                                            `}
                                        >
                                            {fields.images.map((item: ImagesModel, index: number) => {
                                                const stableKey =
                                                    item.fields.image ||
                                                    item.fields.altText ||
                                                    item.fields.link?.value ||
                                                    String(index);

                                                return (
                                                    <StyledWrapper
                                                        key={stableKey}
                                                        {...styles.carouselSlide}
                                                        className="gallery-item"
                                                    >
                                                        {item.fields.image && (
                                                            (item.fields.link && item.fields.link.value && imageLinks.links[index]?.url) ? (
                                                                <Link href={imageLinks.links[index].url} {...styles.imageLink}>
                                                                    <LazyImage
                                                                        src={item.fields.image}
                                                                        altText={item.fields.altText || `Gallery image ${index + 1}`}
                                                                        {...styles.image}
                                                                        className="gallery-image"
                                                                    />
                                                                </Link>
                                                            ) : (
                                                                <LazyImage
                                                                    src={item.fields.image}
                                                                    altText={item.fields.altText || `Gallery image ${index + 1}`}
                                                                    {...styles.image}
                                                                    className="gallery-image"
                                                                />
                                                            )
                                                        )}
                                                    </StyledWrapper>
                                                );
                                            })}
                                        </StyledWrapper>
                                    </StyledWrapper>

                                    {canScrollPrev && (
                                        <StyledWrapper {...styles.prevArrowWrapper}>
                                            <Button
                                                {...styles.prevArrowButton}
                                                onClick={() => embla && embla.scrollPrev()}
                                                disabled={!canScrollPrev}
                                                aria-label="Previous image"
                                            >
                                                {isGlobalViewsTheme ? (
                                                    <ButtonIcon src={GVLeftArrow} />
                                                ) : (
                                                    <ButtonIcon src={ChevronLeft} />
                                                )}
                                            </Button>
                                        </StyledWrapper>
                                    )}
                                    {canScrollNext && (
                                        <StyledWrapper {...styles.nextArrowWrapper}>
                                            <Button
                                                {...styles.nextArrowButton}
                                                onClick={() => embla && embla.scrollNext()}
                                                disabled={!canScrollNext}
                                                aria-label="Next image"
                                            >
                                                {isGlobalViewsTheme ? (
                                                    <ButtonIcon src={GVRightArrow} />
                                                ) : (
                                                    <ButtonIcon src={ChevronRight} />
                                                )}
                                            </Button>
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                            </Hidden>
                        </>
                    )}

                    {(!fields.images || fields.images.length === 0) && (
                        <StyledWrapper
                            css={css`
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                min-height: 200px;
                                background-color: #f5f5f5;
                                color: #666;
                                text-align: center;
                                padding: 40px 20px;
                            `}
                        >
                            <div>
                                <h3 style={{ margin: '0 0 15px 0', fontSize: '24px', color: '#333' }}>
                                    Image Gallery Widget
                                </h3>
                                <p style={{ margin: '0 0 10px 0', fontSize: '16px' }}>
                                    No images have been added yet.
                                </p>
                                <p style={{ margin: '0', fontSize: '14px' }}>
                                    Edit this widget to add a title and gallery images.
                                </p>
                            </div>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 3,
};

const SurImageGalleryWidgetModule: WidgetModule = {
    component: SurImageGalleryWidget,
    definition: {
        group: "Basic",
        icon: "image",
        displayName: "Sur Image Gallery",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Gallery Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
                sortOrder: 0,
            },
            {
                name: fields.images,
                displayName: "Gallery Images",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.altText || "Gallery Image";
                },
                defaultValue: [],
                fieldType: "General",
                tab: contentTab,
                sortOrder: 1,
                fieldDefinitions: [
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        sortOrder: 0,
                    },
                    {
                        name: "altText",
                        displayName: "Alt Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 1,
                    },
                    {
                        name: "link",
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        sortOrder: 2,
                    },
                ],
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                tab: settingsTab,
                sortOrder: 0,
            },
            {
                name: fields.textColor,
                displayName: "Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                tab: settingsTab,
                sortOrder: 1,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse CSS";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default SurImageGalleryWidgetModule;